import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, Zap, Bell, ArrowUpDown, PlaneTakeoff, Pencil, ChevronDown } from "lucide-react";
import SearchBar from "../components/SearchBar";
import FlightCard from "../components/FlightCard";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import { searchAvailability, resolveAirport, CABINS, fmtDate, isDateLocked, FREE_WINDOW_DAYS } from "../data/mock";
import ProGate, { useIsPro } from "../components/ProGate";

export default function Search() {
  const [params] = useSearchParams();
  const from = params.get("from");
  const to = params.get("to");
  const date = params.get("date") || new Date().toISOString().slice(0, 10);
  const flex = Number(params.get("flex") || 3);

  const [cabinFilter, setCabinFilter] = useState([]);
  const [editing, setEditing] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sort, setSort] = useState("points");
  const [maxPoints, setMaxPoints] = useState(200000);

  const isPro = useIsPro();
  const hasQuery = from && to;
  const oA = hasQuery ? resolveAirport(from) : null;
  const dA = hasQuery ? resolveAirport(to) : null;

  const raw = useMemo(() => {
    if (!hasQuery) return [];
    return searchAvailability({ origin: from, destination: to, date, flexDays: flex });
  }, [from, to, date, flex, hasQuery]);

  const results = useMemo(() => {
    let r = [...raw];
    if (cabinFilter.length) r = r.filter((x) => cabinFilter.includes(x.cabin));
    r = r.filter((x) => x.points <= maxPoints);
    if (sort === "points") r.sort((a, b) => a.points - b.points);
    else if (sort === "date") r.sort((a, b) => new Date(a.date) - new Date(b.date));
    else if (sort === "duration") r.sort((a, b) => a.distance - b.distance);
    return r;
  }, [raw, cabinFilter, sort, maxPoints]);

  const free = useMemo(() => results.filter((x) => !isDateLocked(x.date, isPro)), [results, isPro]);
  const locked = useMemo(() => results.filter((x) => isDateLocked(x.date, isPro)), [results, isPro]);

  const toggleCabin = (k) =>
    setCabinFilter((prev) => (prev.includes(k) ? prev.filter((c) => c !== k) : [...prev, k]));

  useEffect(() => { setEditing(false); }, [from, to, date, flex]);
  const activeFilters = cabinFilter.length + (maxPoints < 200000 ? 1 : 0);

  return (
    <main className="min-h-[70vh] bg-slate-50">
      <Seo
        title={hasQuery ? `${from} to ${to} award availability | FindMyFlight` : "Award flight search | FindMyFlight"}
        description="Search award availability across mileage programs with instant results."
        path="/search"
      />
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-4 sm:py-6">
          {hasQuery && !editing && (
            <button onClick={() => setEditing(true)} data-testid="mobile-search-summary" className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left shadow-sm sm:hidden">
              <span>
                <span className="block text-base font-bold text-slate-900">{from} → {to}</span>
                <span className="block text-xs text-slate-500">{fmtDate(date)} · ±{flex} days</span>
              </span>
              <span className="flex items-center gap-1 text-sm font-semibold text-blue-600"><Pencil className="h-4 w-4" /> Edit</span>
            </button>
          )}
          <div className={hasQuery && !editing ? "hidden sm:block" : ""}>
            <SearchBar defaultTab="search" onSubmit={() => setEditing(false)} />
          </div>
        </div>
      </div>

      {!hasQuery ? (
        <div className="mx-auto max-w-6xl px-5 py-24 text-center">
          <PlaneTakeoff className="mx-auto h-12 w-12 text-blue-500" />
          <h2 className="mt-4 text-2xl font-bold text-slate-900">Start your award search</h2>
          <p className="mt-2 text-slate-600">Pick an origin, destination and date above to see live availability across programs.</p>
        </div>
      ) : (
        <div className="mx-auto max-w-6xl gap-6 px-5 py-8 lg:flex">
          {/* Filters */}
          <aside className="mb-4 shrink-0 lg:mb-0 lg:w-64">
            <button onClick={() => setFiltersOpen((o) => !o)} data-testid="mobile-filters-toggle" className="flex h-11 w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-900 lg:hidden">
              <span className="flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" /> Filters{activeFilters > 0 && <span className="rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-bold text-white">{activeFilters}</span>}</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
            </button>
            <div className={`rounded-2xl border border-slate-200 bg-white p-5 ${filtersOpen ? "mt-3 block" : "hidden"} lg:mt-0 lg:block`}>
              <div className="hidden items-center gap-2 text-sm font-bold text-slate-900 lg:flex"><SlidersHorizontal className="h-4 w-4" /> Filters</div>
              <div className="lg:mt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Cabin</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {CABINS.map((c) => (
                    <button key={c.key} onClick={() => toggleCabin(c.key)} className={`rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors ${cabinFilter.includes(c.key) ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-blue-300"}`}>{c.label}</button>
                  ))}
                </div>
              </div>
              <div className="mt-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Max points: {(maxPoints/1000)}k</p>
                <input type="range" min={20000} max={200000} step={5000} value={maxPoints} onChange={(e) => setMaxPoints(Number(e.target.value))} className="mt-2 w-full accent-blue-600" />
              </div>
              <Link to="/alerts" className="mt-5 block">
                <Button variant="outline" className="w-full gap-2"><Bell className="h-4 w-4" /> Create alert</Button>
              </Link>
            </div>
          </aside>

          {/* Results */}
          <div className="flex-1">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h1 className="text-xl font-bold text-slate-900">{oA?.city} → {dA?.city}</h1>
                <p className="text-sm text-slate-500">{results.length} results · {fmtDate(date)} · ±{flex} days · {oA && dA ? `${new Intl.NumberFormat().format(raw[0]?.distance || 0)} mi` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <ArrowUpDown className="h-4 w-4 text-slate-400" />
                <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select" className="h-10 rounded-lg border border-slate-200 bg-white px-2 text-sm outline-none focus:border-blue-500">
                  <option value="points">Lowest points</option>
                  <option value="date">Earliest date</option>
                  <option value="duration">Shortest distance</option>
                </select>
              </div>
            </div>
            {results.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center">
                <Zap className="mx-auto h-10 w-10 text-slate-300" />
                <p className="mt-3 font-semibold text-slate-700">No availability matches your filters</p>
                <p className="text-sm text-slate-500">Try widening your date flex or raising the max points.</p>
              </div>
            ) : (
              <>
                {free.length > 0 && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="search-results-grid">
                    {free.map((d) => <FlightCard key={d.id} d={d} showFresh />)}
                  </div>
                )}
                {locked.length > 0 && (
                  <div className={free.length ? "mt-8" : ""}>
                    {free.length > 0 && <p className="mb-3 text-sm font-semibold text-slate-700" data-testid="locked-results-heading">{locked.length} more result{locked.length === 1 ? "" : "s"} beyond the next {FREE_WINDOW_DAYS} days</p>}
                    <ProGate locked count={locked.length}>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                        {locked.slice(0, 6).map((d) => <FlightCard key={d.id} d={d} showFresh savable={false} />)}
                      </div>
                    </ProGate>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
